import { apiRequest } from '@/lib/apiClient';

export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const VIDEO_MAX_BYTES = 50 * 1024 * 1024;
export const VIDEO_MAX_SECONDS = 60;
const IMAGE_LONG_EDGE = 2048;

export type MediaUploadResult = {
  mediaUrl: string;
  mediaType: 'image' | 'video';
  size: number;
};

export async function prepareMedia(file: File): Promise<File> {
  if (file.type.startsWith('image/')) return optimizeImage(file);
  if (file.type.startsWith('video/')) {
    if (file.size > VIDEO_MAX_BYTES) throw new Error('Videos must be 50 MB or smaller.');
    const duration = await videoDuration(file);
    if (duration > VIDEO_MAX_SECONDS + 0.25) throw new Error('Videos must be 60 seconds or shorter.');
    return file;
  }
  throw new Error('Choose a photo or video file.');
}

export async function uploadMedia(file: File, challengeId: string): Promise<MediaUploadResult> {
  const prepared = await prepareMedia(file);
  const form = new FormData();
  form.append('file', prepared);
  form.append('challengeId', challengeId);
  return apiRequest<MediaUploadResult>('/media/upload', { method: 'POST', body: form });
}

async function optimizeImage(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, IMAGE_LONG_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Unable to prepare this image.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error('Unable to optimize this image.'));
    }, 'image/webp', 0.88);
  });
  if (blob.size > PHOTO_MAX_BYTES) throw new Error('Photo is still larger than 10 MB after optimization.');
  return new File([blob], `${stripExtension(file.name)}.webp`, { type: 'image/webp', lastModified: Date.now() });
}

function videoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = video.duration;
      URL.revokeObjectURL(url);
      if (Number.isFinite(duration)) resolve(duration);
      else reject(new Error('Unable to read video duration.'));
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read this video.'));
    };
    video.src = url;
  });
}

function stripExtension(name: string): string {
  return name.replace(/\.[^.]+$/, '') || 'photo';
}
